/**
 * 双星核·并轨的纯逻辑（P3b，design §11.12；公式是 Codex 推导、另写脚本复算过的）：
 * - 玫瑰星在屏幕下方正中（0.5, 0.9）静止亮起，沿盘面（y = 0）上的直线径向飞向中心，
 *   到入口半径 R_i 不停顿地弯进收紧的螺线、绕 1.2 圈，T 时刻和金星锁定成对径绕转
 *   （半径 b、36 秒一圈；金星在飞入段待在中心，进螺线以后对径地出来）。
 * - 难点是两段尺度差几十倍（全景时起点离中心可达上百个单位，绕转半径只有 3.5）：
 *   飞入段按屏幕弧长 ℓ 排时间，螺线段按玫瑰的世界弧长排速度（巡航 V、最后五次平滑减速到 b·ω）。
 * - 全程一条曲线：T_A（飞入→螺线）和 T（锁定）两处位置、速度、加速度都连续——
 *   锁定时两颗星的速度是 b·ω 的切向、加速度是 b·ω² 的向心，和绕转完全一致（不是 0）。
 * 图层（两颗星、残影怎么画）在 twincore.ts；模式、镜头接线、「来过」在 galaxy.ts。
 * 不碰 three.js，好写单元测试。
 */
import type { Vec3 } from '@star-sea/shared';

// ── 数字都集中在这一节：浏览器里看效果时调 ──
/** 绕转半径 b（单位）：暂定 3.5——1.5 在全景下看不清（design §11.12） */
export const TWIN_R = 3.5;
/** 绕转角速度：36 秒一圈 */
export const TWIN_OMEGA = (Math.PI * 2) / 36;
/** 并轨的螺线收这么多圈 */
const DELTA = Math.PI * 2 * 1.2;
/** 接缝处和螺线巡航的世界速度（单位/秒）；图层按它把残影随减速淡掉 */
export const TWIN_CRUISE = 18;
const V = TWIN_CRUISE;
/** 飞入段屏幕速度上限（屏高/秒） */
const SCREEN_LIMIT = 0.598;
/** 飞入段时长：从 MIN_FLIGHT 起每次加 FLIGHT_STEP，取第一个屏幕速度不超上限的，最多到 MAX_FLIGHT */
const MIN_FLIGHT = 1.0;
const MAX_FLIGHT = 3.0;
const FLIGHT_STEP = 0.005;
/** 螺线最后的五次平滑减速最短这么久（再短会有急刹感） */
const MIN_BRAKE = 0.55;
/** 并轨全程的最短时长 */
const MIN_TOTAL = 3.2;
/** 入口半径 R_i = clamp(RI_K × 镜头离原点的距离, RI_MIN, RI_MAX) */
const RI_K = 0.085;
const RI_MIN = 8;
const RI_MAX = 11.5;
/** 起点在屏幕上的位置（画面比例）：下方正中 */
const START_X = 0.5;
const START_Y = 0.9;
/** 螺线弧长表的均匀区间数（复合 Simpson 建表） */
const ARC_N = 2048;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
/** smootherstep：H(0) = 0、H(1) = 1，两端一二阶导数都是 0 */
const H = (u: number) => u * u * u * (10 - 15 * u + 6 * u * u);
/** I(u) = ∫₀ᵘ H = 2.5u⁴ − 3u⁵ + u⁶ */
const IU = (u: number) => u ** 4 * (2.5 - 3 * u + u * u);

const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const cross = (a: Vec3, b: Vec3): Vec3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
const norm = (a: Vec3): Vec3 => {
  const l = Math.hypot(a.x, a.y, a.z) || 1;
  return { x: a.x / l, y: a.y / l, z: a.z / l };
};
/** layout.ts 的角度约定：e(θ) = (sin θ, 0, −cos θ)——θ = 0 在 −Z（从上往下看的正上方），顺时针增加 */
const dir = (theta: number): Vec3 => ({ x: Math.sin(theta), y: 0, z: -Math.cos(theta) });

/**
 * 纯函数要的镜头参数：位置 C、注视点 Q、竖直视角（度）、宽高比，再加镜头的右方向——
 * 正上方往下看时只靠位置和注视点定不了屏幕朝向，所以一律传它（galaxy 里从 matrixWorld 第一列取）。
 */
export interface TwinCamera {
  pos: Vec3;
  target: Vec3;
  fov: number;
  aspect: number;
  right: Vec3;
}

/** 镜头的 f̂、r̂、û（û = r̂ × f̂ 是镜头的「上」）和 h = tan(fov/2) */
function basis(cam: TwinCamera): { f: Vec3; r: Vec3; u: Vec3; h: number } {
  const f = norm(sub(cam.target, cam.pos));
  const r = norm(cam.right);
  return { f, r, u: cross(r, f), h: Math.tan(((cam.fov / 2) * Math.PI) / 180) };
}

/** 起点：屏幕 (START_X, START_Y) 的视线和盘面（y = 0）的交点 P_s——起点半径不夹 */
export function launchStart(cam: TwinCamera): Vec3 {
  const { f, r, u, h } = basis(cam);
  // NDC (2x−1, 1−2y)；视线 D = f̂ + ndcY·h·û + ndcX·h·aspect·r̂
  const nx = 2 * START_X - 1,
    ny = 1 - 2 * START_Y;
  const d = {
    x: f.x + ny * h * u.x + nx * h * cam.aspect * r.x,
    y: f.y + ny * h * u.y + nx * h * cam.aspect * r.y,
    z: f.z + ny * h * u.z + nx * h * cam.aspect * r.z,
  };
  if (!(d.y < -1e-9)) throw new Error('并轨起点的视线打不到盘面上');
  const k = -cam.pos.y / d.y;
  return { x: cam.pos.x + d.x * k, y: 0, z: cam.pos.z + d.z * k };
}

/** 世界点投影回屏幕比例坐标（0–1）：校对起点和飞入段屏幕速度用；Π 本身是「以屏高为单位、中心为原点」 */
export function launchScreen(cam: TwinCamera, p: Vec3): { x: number; y: number } {
  const { f, r, u, h } = basis(cam);
  const w = sub(p, cam.pos),
    dep = dot(f, w);
  return { x: dot(r, w) / (2 * h * dep * cam.aspect) + 0.5, y: -dot(u, w) / (2 * h * dep) + 0.5 };
}

/** 两颗星的位置（世界坐标，y 都是 0） */
export interface TwinPos {
  gold: Vec3;
  rose: Vec3;
}
const newPos = (): TwinPos => ({ gold: { x: 0, y: 0, z: 0 }, rose: { x: 0, y: 0, z: 0 } });

/**
 * 冻结好的并轨轨迹：prepareLaunch 按当时的镜头算一次，之后任意 t 查询（launchTrack）。
 * t = 0 是并轨开始，[0, ta) 飞入段、[ta, total) 螺线段、t ≥ total 锁定后的绕转。
 */
export interface MergeTrack {
  /** 起点 P_s（盘面上）、起点半径、入口半径、起点方向角 */
  start: Vec3;
  rs: number;
  ri: number;
  theta0: number;
  /** 锁定角 θ0 + Δ；t ≥ total 后绕转相位 θ = thetaEnd + ω·(t − total) */
  thetaEnd: number;
  /** 飞入段时长、锁定时刻（并轨总时长） */
  ta: number;
  total: number;
  /** 螺线段玫瑰路径长、最后减速的时长、巡航的时长 */
  lb: number;
  brake: number;
  cruise: number;
  /** 绕转半径、角速度、锁定时的末速 b·ω */
  b: number;
  omega: number;
  vf: number;
  /** 飞入段：径向单位向量 E（y = 0），投影度量 Z(r) = c·r + d、K、起点处 Z */
  ex: number;
  ez: number;
  c: number;
  d: number;
  k: number;
  zs: number;
  /** 飞入段：屏幕总长 L_A、ℓ(u) = c3·u³ + c4·u⁴ + c5·u⁵ */
  la: number;
  c3: number;
  c4: number;
  c5: number;
  /** 螺线弧长表：q 均匀取 ARC_N 个区间的累计弧长（复合 Simpson）。要双精度：单精度的舍入（约 1e-6）会在每个节点上让位置跳一下 */
  arc: Float64Array;
}

/** 玫瑰在螺线上 q 处的弧长导数：g(q) = √(r′² + r²θ′²) */
function spiralG(ri: number, b: number, q: number): number {
  const om = 1 - q,
    rp = -3 * (ri - b) * om * om,
    th = DELTA * (6 * q * q - 4 * q * q * q),
    r = b + (ri - b) * om * om * om;
  return Math.hypot(rp, r * th);
}

/**
 * 飞入段的排时：ℓ(t) 是五次 Hermite（起步速度、加速度为 0，到入口时屏幕速度、加速度
 * 正好对应世界匀速 −V·E、世界加速度 0）。写成 ℓ = c3u³ + c4u⁴ + c5u⁵ 后，
 * ℓ̇ = (3c3u² + 4c4u³ + 5c5u⁴)/T_A 的极值只在 u = 0、1 和 20c5u² + 12c4u + 6c3 = 0
 * 在 (0, 1) 里的根上——检查不靠采样。
 */
function flightCoef(la: number, ue: number, ae: number, T: number): [number, number, number] {
  return [10 * la - 4 * T * ue + 0.5 * T * T * ae, -15 * la + 7 * T * ue - T * T * ae, 6 * la - 3 * T * ue + 0.5 * T * T * ae];
}

/** 这组系数下整段屏幕速度是否在 [0, SCREEN_LIMIT] 内 */
function flightFits(c3: number, c4: number, c5: number, T: number): boolean {
  const spd = (x: number) => (3 * c3 * x * x + 4 * c4 * x ** 3 + 5 * c5 * x ** 4) / T;
  let lo = Math.min(spd(0), spd(1)),
    hi = Math.max(spd(0), spd(1));
  const A = 20 * c5,
    B = 12 * c4,
    C = 6 * c3;
  const roots: number[] = [];
  if (Math.abs(A) > 1e-12) {
    const disc = B * B - 4 * A * C;
    if (disc > 0) {
      const sq = Math.sqrt(disc);
      roots.push((-B + sq) / (2 * A), (-B - sq) / (2 * A));
    }
  } else if (Math.abs(B) > 1e-12) roots.push(-C / B);
  for (const x of roots) {
    if (x <= 0 || x >= 1) continue;
    const v = spd(x);
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  return lo >= -1e-9 && hi <= SCREEN_LIMIT;
}

/** A(q) = ∫₀^q g：查表定到所在区间，再补一段 Simpson——和建表同一张表，Newton 迭代用它才准 */
function arcAt(tr: MergeTrack, q: number): number {
  const x = clamp(q, 0, 1) * ARC_N,
    i = Math.min(ARC_N - 1, Math.floor(x)),
    q0 = i / ARC_N;
  return tr.arc[i]! + ((q - q0) / 6) * (spiralG(tr.ri, tr.b, q0) + 4 * spiralG(tr.ri, tr.b, (q0 + q) / 2) + spiralG(tr.ri, tr.b, q));
}

/**
 * q = A⁻¹(s)：查表定区间和初值，再带区间保护的 Newton。
 * 不要用分段线性插值当最终结果——表格节点会带出速度折点。
 */
function invA(tr: MergeTrack, s: number): number {
  if (s <= 0) return 0;
  if (s >= tr.lb) return 1;
  let lo = 0,
    hi = ARC_N;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (tr.arc[mid]! <= s) lo = mid;
    else hi = mid;
  }
  let qLo = lo / ARC_N,
    qHi = hi / ARC_N,
    q = qLo + ((qHi - qLo) * (s - tr.arc[lo]!)) / Math.max(1e-12, tr.arc[hi]! - tr.arc[lo]!);
  // 先看收敛、再做区间保护：已经收敛时 Newton 这一步约等于 0，新值贴在区间端点上，
  // 要是先判「不在区间里」就会退回二分、把 q 推离已经找到的根（实测残差卡在 5e-9）
  for (let it = 0; it < 40; it++) {
    const f = arcAt(tr, q) - s;
    if (Math.abs(f) <= 1e-12) break;
    if (f > 0) qHi = q;
    else qLo = q;
    const g = spiralG(tr.ri, tr.b, q);
    let qn = g > 1e-9 ? q - f / g : (qLo + qHi) / 2;
    if (!(qn > qLo && qn < qHi)) qn = (qLo + qHi) / 2;
    if (qn === q) break;
    q = qn;
  }
  return clamp(q, 0, 1);
}

/**
 * 按当时的镜头冻结一段并轨：起点、入口半径、飞入段的 ℓ(u) 和时长、螺线段的弧长表和减速安排。
 * radius / omega 缺省是 TWIN_R / TWIN_OMEGA；越界的参数（减速不够长、巡航出负数）宁可抛错，不默默出负数。
 */
export function prepareLaunch(cam: TwinCamera, opts: { radius?: number; omega?: number } = {}): MergeTrack {
  const b = opts.radius ?? TWIN_R,
    omega = opts.omega ?? TWIN_OMEGA;
  const { f, r, u, h } = basis(cam);
  const start = launchStart(cam);
  const rs = Math.hypot(start.x, start.z);
  const ri = clamp(RI_K * Math.hypot(cam.pos.x, cam.pos.y, cam.pos.z), RI_MIN, RI_MAX);
  if (!(rs > ri)) throw new Error('并轨的起点在入口半径以内');
  const ex = start.x / rs,
    ez = start.z / rs,
    theta0 = Math.atan2(start.x, -start.z);

  // 飞入段的投影度量：径向直线 r·E 在屏幕上的弧长元素是 J(r)·|dr|，J(r) = K/Z(r)²
  const ed = { x: ex, y: 0, z: ez };
  const ax = dot(r, ed) / (2 * h),
    ay = -dot(u, ed) / (2 * h),
    bx = -dot(r, cam.pos) / (2 * h),
    by = dot(u, cam.pos) / (2 * h),
    c = dot(f, ed),
    d = -dot(f, cam.pos),
    zAt = (rr: number) => c * rr + d,
    zs = zAt(rs),
    zi = zAt(ri),
    k = Math.hypot(ax * d - bx * c, ay * d - by * c),
    la = (k * (rs - ri)) / (zs * zi);
  // 入口处的屏幕速度 U_e、屏幕加速度 A_e：世界匀速 −V·E 投影到屏幕上，屏幕速度在变，A_e 不是 0
  const ue = (V * k) / (zi * zi),
    ae = (2 * c * k * V * V) / zi ** 3; // = −V²·J′(R_i)，J′ = −2cK/Z³
  // T_A：从 1 秒起每次加 5ms，取第一个「整段屏幕速度在 [0, 0.598] 内」的（最多到 3 秒）
  let ta = MAX_FLIGHT,
    found = false;
  for (let cand = MIN_FLIGHT; cand <= MAX_FLIGHT + 1e-9 && !found; cand += FLIGHT_STEP) {
    const [w3, w4, w5] = flightCoef(la, ue, ae, cand);
    if (flightFits(w3, w4, w5, cand)) {
      ta = cand;
      found = true;
    }
  }
  const [c3, c4, c5] = flightCoef(la, ue, ae, ta);

  // 螺线段的弧长表（复合 Simpson）：玫瑰路径 q∈[0,1]，r_R = b + (R_i − b)(1−q)³、θ = θ0 + Δ(2q³ − q⁴)
  const arc = new Float64Array(ARC_N + 1);
  for (let i = 0; i < ARC_N; i++) {
    const q0 = i / ARC_N,
      q1 = (i + 1) / ARC_N;
    arc[i + 1] = arc[i]! + ((q1 - q0) / 6) * (spiralG(ri, b, q0) + 4 * spiralG(ri, b, (q0 + q1) / 2) + spiralG(ri, b, q1));
  }
  const lb = arc[ARC_N]!,
    vf = b * omega;
  // 总时长不短于 MIN_TOTAL：路短时把减速拉长（D 变长、巡航不变），而不是缩得比 MIN_BRAKE 短
  const tbMin = (lb + 0.5 * (V - vf) * MIN_BRAKE) / V,
    total = Math.max(MIN_TOTAL, ta + tbMin),
    tb = total - ta,
    brake = (2 * (V * tb - lb)) / (V - vf),
    cruise = Math.max(0, tb - brake);
  if (!(brake >= MIN_BRAKE - 1e-9) || !(tb - brake >= -1e-9))
    throw new Error(`并轨参数越界：减速 ${brake.toFixed(3)} 秒、巡航 ${(tb - brake).toFixed(3)} 秒`);

  return {
    start,
    rs,
    ri,
    theta0,
    thetaEnd: theta0 + DELTA,
    ta,
    total,
    lb,
    brake,
    cruise,
    b,
    omega,
    vf,
    ex,
    ez,
    c,
    d,
    k,
    zs,
    la,
    c3,
    c4,
    c5,
    arc,
  };
}

/** 绕转相位 θ 处两颗星的位置：玫瑰在 θ、金在 θ + π（对径，e(θ + π) = −e(θ)） */
export function twinOrbit(theta: number, b: number, out: TwinPos = newPos()): TwinPos {
  const ed = dir(theta);
  out.rose.x = ed.x * b;
  out.rose.y = 0;
  out.rose.z = ed.z * b;
  out.gold.x = -ed.x * b;
  out.gold.y = 0;
  out.gold.z = -ed.z * b;
  return out;
}

/**
 * 并轨轨迹上 t 时刻两颗星的位置：纯函数，按绝对时间求，不逐帧累加，
 * 任意顺序查同一个 t 结果一样（残影就是按 t − i·0.035 往回查的）。
 */
export function launchTrack(tr: MergeTrack, t: number, out: TwinPos = newPos()): TwinPos {
  if (t <= 0) {
    out.gold.x = out.gold.y = out.gold.z = 0;
    out.rose.x = tr.start.x;
    out.rose.y = 0;
    out.rose.z = tr.start.z;
    return out;
  }
  if (t < tr.ta) {
    // 飞入段：屏幕弧长 ℓ(u) → 反解半径 r(ℓ) → 径向直线 r·E；金星待在中心
    const u = t / tr.ta,
      l = tr.c3 * u ** 3 + tr.c4 * u ** 4 + tr.c5 * u ** 5,
      r = (tr.k * tr.rs - l * tr.zs * tr.d) / (tr.k + l * tr.zs * tr.c);
    out.rose.x = tr.ex * r;
    out.rose.y = 0;
    out.rose.z = tr.ez * r;
    out.gold.x = out.gold.y = out.gold.z = 0;
    return out;
  }
  if (t < tr.total) {
    // 螺线段：τ → s（巡航 V、最后五次平滑减速到 vf = b·ω）→ q = A⁻¹(s) → 两颗星的位置；
    // 金星对径地出来：−b·H(q)·e(θ)
    const tau = t - tr.ta;
    let s: number;
    if (tau <= tr.cruise) s = V * tau;
    else {
      const x = clamp((tau - tr.cruise) / tr.brake, 0, 1);
      s = V * tr.cruise + tr.brake * (V * x + (tr.vf - V) * IU(x));
    }
    const q = invA(tr, s),
      om = 1 - q,
      rr = tr.b + (tr.ri - tr.b) * om * om * om,
      th = tr.theta0 + DELTA * (2 * q * q * q - q ** 4),
      ed = dir(th),
      rg = tr.b * H(q);
    out.rose.x = ed.x * rr;
    out.rose.y = 0;
    out.rose.z = ed.z * rr;
    out.gold.x = -ed.x * rg;
    out.gold.y = 0;
    out.gold.z = -ed.z * rg;
    return out;
  }
  // 锁定以后：θ = θ_end + ω(t − T)，相位接上、不重置
  return twinOrbit(tr.thetaEnd + tr.omega * (t - tr.total), tr.b, out);
}
